import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import NavRoutesPermissions from "core/app/util/NavRoutesPermissions";
import { Loader } from "@mediamath/design-system-eng";
import useUserPermissions from "core/shared/hooks/useUserPermissions";
import { useFeatureToggle } from "@flopflip/react-broadcast";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";

function renderComponent({
  component: Component,
  render,
  children,
  routeProps,
}) {
  if (Component) {
    return <Component {...routeProps} />;
  }
  if (render) {
    return render(routeProps);
  }
  return children;
}

const StWaitLoader = styled("div")`
  background: none;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PermissionRouteWrapper = ({
  auth = false,
  children,
  component,
  flag,
  render,
  routeProps,
}) => {
  const userPermissions = useUserPermissions();
  const flagValue = useFeatureToggle(flag);
  const { isAuthenticated, isLoading } = useAuth0();
  const [hasPermission, setHasPermission] = useState(true);
  const routePath = routeProps.match.path;
  const isSharedPreviewRoute = routePath.includes("/creative-preview/");

  useEffect(() => {
    let permission;
    const navRoutesPermission = NavRoutesPermissions[routePath];

    if (navRoutesPermission) {
      const { entity, isCreateMode } = navRoutesPermission;
      const { create, edit, view } = userPermissions[entity] || {};

      permission = isCreateMode ? create : create || edit || view;
    }

    // Route requires login and user is not logged in
    if (auth && !isAuthenticated) permission = false;

    // Route requires a feature flag and user does not have the value
    if (flag && !flagValue) permission = false;

    if (permission !== undefined) setHasPermission(permission);
  }, [auth, flag, flagValue, isAuthenticated, routePath, userPermissions]);

  // if we're loading, show the spinner
  if (
    (auth && isLoading) ||
    (!isSharedPreviewRoute && isEmpty(userPermissions))
  ) {
    return (
      <StWaitLoader>
        <Loader active size="tiny" />
      </StWaitLoader>
    );
  }

  return hasPermission ? (
    renderComponent({ children, component, render, routeProps })
  ) : (
    <Redirect to="/access-denied" />
  );
};

PermissionRouteWrapper.propTypes = {
  auth: PropTypes.bool, // whether route requires authentication
  children: PropTypes.node,
  component: PropTypes.elementType,
  flag: PropTypes.string, // feature flag this route requires
  render: PropTypes.func,
  routeProps: PropTypes.shape({
    match: PropTypes.shape({ path: PropTypes.string }),
  }),
};

export default PermissionRouteWrapper;
